import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Stack,
    IconButton
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

function DataExport() {
    const { api, currentUser } = useAuth();
    const navigate = useNavigate();

    const [ledgers, setLedgers] = useState([]);
    const [selectedLedger, setSelectedLedger] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Protect route strictly
    useEffect(() => {
        if (currentUser && !currentUser.is_super_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    useEffect(() => {
        const fetchLedgers = async () => {
            try {
                const response = await api.get('/ledger/ledgers');
                setLedgers(response.data);
                if (response.data.length > 0) {
                    setSelectedLedger(response.data[0].id);
                }
            } catch (err) {
                setError('Failed to load books');
                console.error(err);
            }
        };

        // Only fetch if super admin
        if (currentUser?.is_super_admin) {
            fetchLedgers();
        }
    }, [api, currentUser]);

    const handleExport = async () => {
        if (!selectedLedger) {
            setError('Please select a book');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('ledger_id', selectedLedger);

        try {
            const response = await api.post('/export/excel', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSuccess(`Export successful! ${response.data.records_added} transactions synced to Dropbox file: ${response.data.file_uploaded}`);

        } catch (err) {
            console.error('Export failed:', err);
            setError(err.response?.data?.detail || 'Failed to export data');
        } finally {
            setLoading(false);
        }
    };

    // Prevent render flash for unauthorized users
    if (!currentUser?.is_super_admin) {
        return null;
    }

    return (
        <Box sx={{ mt: 4, mb: 4, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Export to Dropbox
                    </Typography>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: 4 }}>
                <Stack spacing={4}>
                    <Typography variant="body1" color="text.secondary">
                        Sync your book transactions directly to the "PV Bookkeeping Main" Dropbox Excel file.
                        This action computes dynamic currency exchanges for missing properties and produces a timestamped copy within the versions folder.
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <FormControl fullWidth required>
                        <InputLabel id="book-select-label">Target Book</InputLabel>
                        <Select
                            labelId="book-select-label"
                            value={selectedLedger}
                            label="Target Book"
                            onChange={(e) => setSelectedLedger(e.target.value)}
                            disabled={loading}
                        >
                            {ledgers.map(ledger => (
                                <MenuItem key={ledger.id} value={ledger.id}>{ledger.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        disabled={!selectedLedger || loading}
                        onClick={handleExport}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FileUploadIcon />}
                        sx={{ py: 1.5 }}
                    >
                        {loading ? 'Exporting to Dropbox...' : 'Export Data'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}

export default DataExport;
